import { productLog } from "common/utils/logging";
import perfNow from "common/utils/perfNow";
import { RandomSourceWithSeed } from "common/utils/random";
import * as THREE from "three";
import { HAVE_IMAGEBITMAP } from "../../constants";
import { floodFill } from "../../imageBuffer/floodFill";
import { MAX_SDF_PATTERNS } from "../designMaterialDefaults";
import type { LoadTextureFunc } from "../loadTextureFuncType";
import { imageBufferFromThreeTexture } from "./imageBufferFromThreeTexture";

export function materialMaskColorAsVec4Uniform(
  maskRandomFloat: number,
  patternIndex: number,
  wrappingCellX: number,
  wrappingCellY: number,
): number[] {
  return [maskRandomFloat, patternIndex / (MAX_SDF_PATTERNS - 1), (wrappingCellX + 2) / 3, (wrappingCellY + 2) / 3];
}

export function materialMaskColorBytes(
  maskRandomUint: number,
  patternIndex: number,
  wrappingCellX: number,
  wrappingCellY: number,
): Uint8Array {
  return new Uint8Array([
    maskRandomUint,
    (patternIndex / (MAX_SDF_PATTERNS - 1)) * 255,
    ((wrappingCellX + 2) / 3) * 255,
    ((wrappingCellY + 2) / 3) * 255,
  ]);
}

/**
 * Generate a mask texture with random mask values and correct wrappingCellX/Y values based on a grayscale bitmap.
 * Values less than 50% brightness are treated as being part of the background.
 * Each contiguously colored region with the same color value forms one random mask region.
 *
 * The mask texture contains 4 channels: random mask ID, pattern index, wrappingCellX and wrappingCellY.
 * See sdfLayout.ts for an explanation of the wrappingCell values.
 * @param maskImgData
 * @param width
 * @param height
 * @returns A mask texture with the data packed to 4 channels.
 */
function generateMaskFromBitmap(
  maskImgData: Uint8ClampedArray<ArrayBuffer> | Uint8Array<ArrayBuffer>,
  width: number,
  height: number,
): THREE.DataTexture {
  const t0 = perfNow();

  const randomSource = new RandomSourceWithSeed(0);

  // Fill in the random mask colors.
  for (let y = 0; y < height; ++y) {
    for (let x = 0; x < width; ++x) {
      const pixelDataOffset = (y * width + x) * 4;
      // If the red channel is > 127, this pixel is part of the mask.
      const isPartOfMask = maskImgData[pixelDataOffset] > 127;
      if (isPartOfMask && maskImgData[pixelDataOffset + 3] === 255) {
        // If alpha is 255, we haven't touched it yet.
        const rand = randomSource.next();
        const maskColor = materialMaskColorBytes(rand * 128 + 128, 0, 0, 0);
        floodFill({
          imgData: maskImgData,
          width,
          height,
          numChannels: 4,
          startX: x,
          startY: y,
          fillColor: maskColor,
          wrapAround: true,
        });
      }
      if (!isPartOfMask) {
        maskImgData[pixelDataOffset] = 0;
        maskImgData[pixelDataOffset + 1] = 0;
        maskImgData[pixelDataOffset + 2] = 0;
        maskImgData[pixelDataOffset + 3] = 0;
      }
    }
  }

  // Add the wrapping information: for each colored region touching the top or left of the mask, color in wrappingCellX / wrappingCellY values.
  // First: wrappingCellY to the top row of pixels
  const pixelWhenNotWrapped = materialMaskColorBytes(0, 0, 0, 0);
  const pixelWhenNotWrappedX = pixelWhenNotWrapped[2];
  const pixelWhenNotWrappedY = pixelWhenNotWrapped[3];
  for (let x = 0; x < width; ++x) {
    const pixelDataOffset = x * 4;
    if (maskImgData[pixelDataOffset + 3] === pixelWhenNotWrappedY) {
      let maskColor: Uint8Array;
      if (x === 0) {
        // Optimization for top left pixel - both wrappingCellX and wrappingCellY are filled in to correct values right away.
        maskColor = materialMaskColorBytes(maskImgData[pixelDataOffset], 0, -1, 1);
      } else {
        maskColor = materialMaskColorBytes(maskImgData[pixelDataOffset], 0, 0, 1);
      }
      floodFill({
        imgData: maskImgData,
        width,
        height,
        numChannels: 4,
        startX: x,
        startY: 0,
        fillColor: maskColor,
        wrapAround: false,
      });
    }
  }

  // Second: wrappingCellX to the left column of pixels
  for (let y = 1; y < height; ++y) {
    const pixelDataOffset = y * width * 4;
    if (maskImgData[pixelDataOffset + 2] === pixelWhenNotWrappedX) {
      const maskColor: Uint8Array = materialMaskColorBytes(
        maskImgData[pixelDataOffset],
        0,
        -1,
        Math.round((maskImgData[pixelDataOffset + 3] / 255) * 2 - 1),
      );
      floodFill({
        imgData: maskImgData,
        width,
        height,
        numChannels: 4,
        startX: 0,
        startY: y,
        fillColor: maskColor,
        wrapAround: false,
      });
    }
  }

  const t1 = perfNow();
  if (t1 - t0 > 100) {
    productLog("Generated mask with flood fill method in " + (t1 - t0) + " ms");
  }

  const maskTexture = new THREE.DataTexture(
    maskImgData,
    width,
    height,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
    THREE.UVMapping,
    THREE.RepeatWrapping,
    THREE.RepeatWrapping,
  );
  maskTexture.flipY = true;
  maskTexture.needsUpdate = true;
  return maskTexture;
}

const maskCache: { [texName: string]: Promise<THREE.DataTexture | null> } = {};

export function getMaskFromBitmap(
  srcTexName: string,
  loadTexture: LoadTextureFunc,
  layerTextureFormat: THREE.PixelFormat,
): Promise<THREE.DataTexture | null> {
  if (maskCache.hasOwnProperty(srcTexName)) {
    return maskCache[srcTexName];
  }

  const maskPromise: Promise<THREE.DataTexture | null> = (async function (texName) {
    const [srcTex, srcTexPromise] = loadTexture(texName, layerTextureFormat);
    if (!srcTex) {
      return null;
    }
    await srcTexPromise;

    const flipY = HAVE_IMAGEBITMAP;
    const maskImgData = imageBufferFromThreeTexture(srcTex, flipY);
    const srcTexImage = srcTex.image as { width: number; height: number };
    const width = srcTexImage.width;
    const height = srcTexImage.height;
    if (!(width === height)) {
      console.assert(false);
    }
    if (!(maskImgData.length === width * height * 4)) {
      console.assert(false);
    }

    const mask = generateMaskFromBitmap(maskImgData, width, height);
    return mask;
  })(srcTexName);

  maskCache[srcTexName] = maskPromise;
  return maskPromise;
}
